import store from '@/store';
import doRequest from '@/api/dssRequest';
import { translate } from '@/translation/native_translate';

export default class PaymentMethod {
  /**
   * Available payment methods
   */
  static MOLLIE = 'Mollie';
  static AT_EVENT = 'AtEvent';
  static BUYING_ON_ACCOUNT = 'BuyingOnAccount';
  static DIRECT_DEBIT = 'DirectDebit';

  /**
   * Available payment method subtypes
   */
  static IDEAL = 'ideal';
  static CREDIT_CARD = 'creditcard';

  /**
   * Payment method. Can be: 'Mollie', 'AtEvent', 'BuyingOnAccount', 'DirectDebit'.
   * Will be received from the BE in an array of objects of all available payment methods.
   * Will be send back to the BE in bookWithReservations, as an object of the selected payment method by the user.
   */
  PaymentMethod = '';
  /**
   * Payment method subtype. Can be: 'ideal', 'creditcard'.
   * Will be received from the BE in an array of objects of all available payment methods.
   * Will be send back to the BE in bookWithReservations, as an object of the selected payment method by the user.
   */
  PaymentMethodSubType = '';
  /**
   * Name to show under, like 'Cash', 'iDEAL'
   * Will be received from the BE in an array of objects of all available payment methods.
   */
  DisplayName = '';
  /**
   * Picture url that represents the payment method.
   * Will be received from the BE in an array of objects of all available payment methods.
   */
  PictureUrl = '';
  /**
   * Payment method info text.
   */
  Info = '';

  constructor(data, directPayment) {
    if (data) {
      this.PaymentMethod = data.PaymentMethod;
      this.PaymentMethodSubType = data.PaymentMethodSubType;
      this.DisplayName = data.DisplayName;
      this.PictureUrl = data.PictureUrl;

      if (
        this.PaymentMethod === PaymentMethod.MOLLIE &&
        this.PaymentMethodSubType === PaymentMethod.IDEAL
      ) {
        if (directPayment) {
          this.Info = translate('PAYMENT_METHOD_IDEAL_INFO_DIRECT');
        } else {
          this.Info = translate('PAYMENT_METHOD_IDEAL_INFO_DELAYED');
        }
      } else if (
        this.PaymentMethod === PaymentMethod.MOLLIE &&
        this.PaymentMethodSubType === PaymentMethod.CREDIT_CARD
      ) {
        if (directPayment) {
          this.Info = translate('PAYMENT_METHOD_CREDIT_CARD_INFO_DIRECT');
        } else {
          this.Info = translate('PAYMENT_METHOD_CREDIT_CARD_INFO_DELAYED');
        }
      } else if (this.PaymentMethod === PaymentMethod.AT_EVENT) {
        this.Info = translate('PAYMENT_METHOD_AT_EVENT_INFO');
      } else if (this.PaymentMethod === PaymentMethod.BUYING_ON_ACCOUNT) {
        this.Info = translate('PAYMENT_METHOD_ON_ACCOUNT_INFO');
      } else if (this.PaymentMethod === PaymentMethod.DIRECT_DEBIT) {
        this.Info = translate('PAYMENT_METHOD_DIRECT_DEBIT_INFO');
      }
    }
  }

  /**
   * Check if current method equals to another one
   * @param {PaymentMethod} method the payment method need to be compared to
   * @returns true if same, otherwise false
   */
  equalTo(method) {
    if (method == null) {
      return false;
    }
    return (
      this.PaymentMethod === method.PaymentMethod &&
      this.PaymentMethodSubType === method.PaymentMethodSubType
    );
  }

  /**
   * Get all payment methods based on user's reservations and selected delivery/pickup event
   * @param {string} userId user id or temporary user id
   * @param {object} reservations current reservations in shopping cart with following structure:\
   *                              { eventId1: [orderId1, orderId2], eventId2: [orderId3] }
   * @param {string} deliveryPickupEventId id of the selected delivery/pickup event. Leave empty if not available
   * @returns array of payment methods if resolved, otherwise error
   */
  static getPaymentMethods(
    userId,
    reservations,
    deliveryPickupEventId,
    shoppingListId,
    subscriptionProducts,
    forSubscription
  ) {
    let deliveryPickupEventIdStr = '';
    if (typeof deliveryPickupEventId === 'number') {
      deliveryPickupEventIdStr = deliveryPickupEventId.toString().trim();
    } else if (typeof deliveryPickupEventId === 'string') {
      deliveryPickupEventIdStr = deliveryPickupEventId.trim();
    }

    const payload = {
      Reservations: reservations,
      DeliveryPickupEventId: deliveryPickupEventIdStr,
      ShoppingListId: shoppingListId,
      SubscriptionProducts: subscriptionProducts,
      ForSubscription: forSubscription,
    };

    return new Promise((resolve, reject) => {
      doRequest(
        (response) => {
          const methodList = [];
          if (response.data.PaymentMethods != null) {
            response.data.PaymentMethods.forEach((method) => {
              var directPayment = true;
              if (forSubscription || shoppingListId) {
                directPayment = false;
              }
              const methodObj = new PaymentMethod(method, directPayment);
              methodList.push(methodObj);
            });
          }
          resolve(methodList);
        },
        reject,
        'getPaymentMethodsV2',
        userId,
        payload
      );
    });
  }

  static getPaymentMethodsForOrder(orderGroupId) {
    let request = '';
    let requestObjId = '';

    if (store.getters['auth/isValid']) {
      // Logged in
      requestObjId = store.getters['auth/userName'];
      request = 'getPaymentMethodsV2';
    } else {
      requestObjId = `${store.getters['Account/partnerGroupDetails'].AccountId}_UM`;
      request = 'getPaymentMethodsV2NoLogin';
    }

    const payload = {
      OrderGroupId: orderGroupId,
    };

    return new Promise((resolve, reject) => {
      doRequest(
        (response) => {
          const methodList = [];
          if (response.data.PaymentMethods != null) {
            response.data.PaymentMethods.forEach((method) => {
              const methodObj = new PaymentMethod(method, true);
              methodList.push(methodObj);
            });
          }

          resolve({
            PaymentMethods: methodList,
            PaymentUrl: response.data.PaymentUrl,
          });
        },
        reject,
        request,
        requestObjId,
        payload
      );
    });
  }
}
